import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import type { ImageFieldValue, LinkTarget } from "../../types";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";
import { StatusBadge } from "./StatusBadge";

export type Activity = {
  title: string;
  description?: string;
  href: LinkTarget;
};

type Props = Activity & {
  imageSrc?: ImageFieldValue;
  imageAlt?: string;
  /** Compact variant: hide the description text and collapse the blue panel to
   *  just the title + arrow. Used by image-led tile grids (e.g. the banknote
   *  gallery) where a tall description panel only adds dead space. Default
   *  (non-compact) cards are unchanged. */
  compact?: boolean;
  /** Pre-rendered title (e.g. search-highlighted <mark> nodes) shown INSTEAD
   *  of `title`. `title` stays required for alt/aria/string logic. */
  titleNode?: ReactNode;
  /** Pastille de statut juridique de la fiche (« En vigueur » / « Texte
   *  historique »), projetée par l'admin sur les textes réglementaires (C.68).
   *  Absente partout ailleurs : la carte ne bouge pas. */
  badge?: { label: string; tone?: string };
};

/** Activity card with image, title, description and navigation link.
 *
 *  When `imageSrc` is empty, the image strip is omitted and the card becomes
 *  a single text panel - no generic placeholder PNG (which historically read
 *  as a broken image on data-record-style pages without editorial photos).
 *  Editorial pages always supply an image, so the visual is unchanged for
 *  them; only image-less cards get the lean text-only variant.
 *
 *  With `compact`, the description is dropped and the panel loses its
 *  `min-height` so it shrinks to the title + arrow - an image-only tile reads
 *  as a tidy caption strip instead of a half-empty box. */
export function ActivityCard({ title, description, href, imageSrc, imageAlt = "Illustration d’activité", compact = false, titleNode, badge }: Props) {
  const { src, options } = resolveImageValue(imageSrc);
  const hasImage = Boolean(src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const showDescription = !compact && Boolean(description && description.trim());
  // Le corps de carte est construit deux fois : avec chevron (carte cliquable) et sans
  // (carte informative dont la cible n'existe pas - elle reste AFFICHÉE, mais rien ne
  // doit suggérer un clic).
  const cardBody = (withArrow: boolean) => (
    <div className={hasImage ? "grid h-full grid-cols-[2fr_3fr] md:flex md:flex-col" : "flex h-full flex-col"}>
      {hasImage ? (
        <div
          className={mergeClassNames(
            "relative min-h-[150px] overflow-hidden bg-slate-100 rounded-l-sm md:min-h-0 md:w-full md:rounded-none md:rounded-t-sm md:rounded-bl-none md:rounded-br-none md:rounded-tr-sm",
            overrideProps.className
          )}
          style={overrideProps.style}
        >
          <RuntimeImage
            src={src as string}
            alt={imageAlt}
            className="absolute inset-0 h-full w-full object-cover object-[center_30%] md:static md:h-56"
            style={objectPositionStyle}
          />
        </div>
      ) : null}
      {compact ? (
        // Compact: title + arrow on a single row - the yellow chevron is pinned
        // to the right of the title, so there's no spare vertical space.
        <div
          className={`flex items-center justify-between gap-3 bg-[#eaf2fb] px-5 py-4 ${
            hasImage ? "rounded-r-md md:rounded-none md:rounded-b-md" : "rounded-md"
          }`}
        >
          <h3 className="text-[18px] xl:text-[20px] md:text-[20px] font-semibold leading-6 text-[#005CA9] line-clamp-2">
            {titleNode ?? title}
          </h3>
          {badge?.label ? <StatusBadge badge={badge} /> : null}
          {withArrow ? <ChevronRight className="h-5 w-5 shrink-0 text-[#F9A21B]" /> : null}
        </div>
      ) : (
        <div
          className={`flex min-h-[150px] flex-col bg-[#eaf2fb] p-4 ${
            hasImage ? "rounded-r-md md:min-h-[180px] md:rounded-none md:rounded-b-md" : "rounded-md md:min-h-[180px]"
          }`}
        >
          <div>
            {badge?.label ? (
              <div className="mb-2">
                <StatusBadge badge={badge} />
              </div>
            ) : null}
            <h3 className="text-[18px] xl:text-[20px] md:text-[20px] font-semibold leading-6 text-[#005CA9] line-clamp-3">
              {titleNode ?? title}
            </h3>
            {showDescription ? (
              <p className="mt-2 line-clamp-2 md:line-clamp-3 text-base xl:text-lg leading-5 text-slate-600">
                {description}
              </p>
            ) : null}
          </div>
          {withArrow ? (
            <div className="mt-auto pt-3 text-[#F9A21B]">
              <ChevronRight className="h-4 w-4" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
  // Lien non connecté : la carte est INFORMATIVE - elle reste affichée sur tous les
  // environnements, sans lien ni chevron (jamais masquée, jamais de renvoi vers la home).
  return (
    <GuardedLink
      href={href}
      label={title}
      block
      fallback="unwrap"
      className="group block h-full bg-white"
      unwrapChildren={cardBody(false)}
    >
      {cardBody(true)}
    </GuardedLink>
  );
}


